/**
 * Utility functions for image processing and optimization
 */

/**
 * Resizes an image while maintaining aspect ratio
 * @param input The image buffer or blob to resize
 * @param maxWidth Maximum width of the resized image
 * @param maxHeight Maximum height of the resized image
 * @param quality JPEG quality (0-100)
 * @param forceSquare Whether to force a square output by cropping
 * @returns Promise<Blob> The resized image as a blob
 */
export const resizeImage = async (
  input: Buffer | Blob,
  maxWidth: number,
  maxHeight: number,
  quality: number = 70,
  forceSquare: boolean = false
): Promise<Blob> => {
  let imageUrl = '';
  try {
    // Create a blob from the input. TypeScript 5's stricter DOM types don't
    // accept a Node Buffer (which has ArrayBufferLike backing) directly as a
    // BlobPart, so copy the bytes into a fresh Uint8Array first.
    const blob = input instanceof Blob
      ? input
      : new Blob([new Uint8Array(input)]);
    imageUrl = URL.createObjectURL(blob);

    // Create an image element
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });

    // Calculate new dimensions while maintaining aspect ratio
    let width = img.width;
    let height = img.height;
    let offsetX = 0;
    let offsetY = 0;
    let targetWidth = width;
    let targetHeight = height;

    if (forceSquare) {
      // For square images, take the smaller dimension
      const size = Math.min(maxWidth, maxHeight);
      if (width > height) {
        // Landscape image
        offsetX = (width - height) / 2;
        width = height;
        targetWidth = targetHeight = size;
      } else {
        // Portrait image
        offsetY = (height - width) / 2;
        height = width;
        targetWidth = targetHeight = size;
      }
    } else {
      // Regular resize maintaining aspect ratio
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      if (ratio < 1) {
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        targetWidth = width;
        targetHeight = height;
      }
    }

    // Create a canvas and draw the resized image
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    if (forceSquare) {
      // Draw the cropped square
      ctx.drawImage(
        img,
        offsetX,
        offsetY,
        width,
        height,
        0,
        0,
        targetWidth,
        targetHeight
      );
    } else {
      // Draw the regular resized image
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    }

    // Convert canvas to blob
    const resizedBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        quality / 100
      );
    });

    return resizedBlob;
  } catch (error) {
    console.error('Error processing image:', error);
    throw error;
  } finally {
    // Clean up
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
  }
};

/**
 * Creates a thumbnail version of an image
 * @param input The image buffer or blob to create a thumbnail from
 * @returns Promise<Blob> The thumbnail as a blob
 */
export const createThumbnail = (input: Buffer | Blob): Promise<Blob> => {
  return resizeImage(input, 400, 400, 80, true);
};

/**
 * Creates a medium-sized version of an image
 * @param input The image buffer or blob to create a medium version from
 * @returns Promise<Blob> The medium-sized image as a blob
 */
export const createMediumImage = (input: Buffer | Blob): Promise<Blob> => {
  return resizeImage(input, 1600, 1600, 90, false);
};

// Cache busting utility for image URLs
export const addCacheBuster = (url: string): string => {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    // Add a timestamp to prevent caching
    urlObj.searchParams.set('cb', Date.now().toString());
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, just return the original URL
    console.warn('Failed to add cache buster to URL:', error);
    return url;
  }
};

// Remove cache buster from URL (for storage operations)
export const removeCacheBuster = (url: string): string => {
  if (!url) return url;
  
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('cb');
    return urlObj.toString();
  } catch (error) {
    console.warn('Failed to remove cache buster from URL:', error);
    return url;
  }
}; 